import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon, ClockIcon, SearchIcon, XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { POPULAR_SEARCHES, TRENDING_PRODUCTS } from '../../data/searchData';
import { trackSearch } from '../../lib/analytics/events';
import { useStore } from '../../lib/store';
interface MobileSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
export function MobileSearchOverlay({
  isOpen,
  onClose
}: MobileSearchOverlayProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [noResultsMessage, setNoResultsMessage] = useState('');
  const {
    searchQuery,
    setSearchQuery,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const matchingProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return TRENDING_PRODUCTS;
    return TRENDING_PRODUCTS.filter(product => `${product.name} ${product.category}`.toLowerCase().includes(query));
  }, [searchQuery]);
  useEffect(() => {
    if (!isOpen) return;
    setNoResultsMessage('');
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 100);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const openProduct = (productId: number) => {
    onClose();
    navigate(`/product/${productId}`);
  };
  const selectSearch = (query: string) => {
    setNoResultsMessage('');
    setSearchQuery(query);
  };
  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    if (matchingProducts[0]) {
      addRecentSearch(query);
      trackSearch(query, 'mobile-search', matchingProducts.length);
      openProduct(matchingProducts[0].id);
      return;
    }
    setNoResultsMessage('Try a different medicine, brand, or health category.');
  };
  return <div className="fixed inset-0 z-[80] bg-white md:hidden" role="dialog" aria-modal="true" aria-label="Search products" data-id="element-5537" data-mp="2ioc7jbdt9svi">
      <header className="border-b border-gray-100 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)]" data-id="element-5538" data-mp="3adf9gki86r4z">
        <form onSubmit={submitSearch} className="flex items-center gap-2" data-id="element-5539" data-mp="35mgv3qgss34d">
          <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl bg-gray-100 px-3 focus-within:ring-2 focus-within:ring-brand/25" data-id="element-5540" data-mp="35kcec7xsv9mo">
            <SearchIcon className="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-5541" data-mp="1yniff5nq4p6" />
            <input ref={inputRef} value={searchQuery} onChange={event => {
            setNoResultsMessage('');
            setSearchQuery(event.target.value);
          }} placeholder="Search medicines and wellness" className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500" aria-label="Search products" data-id="element-5542" data-mp="3tc1da754mwcl" />
            {searchQuery && <button type="button" onClick={() => setSearchQuery('')} aria-label="Clear search" className="rounded-md p-1 text-gray-500" data-id="element-5543" data-mp="1yt149abcn64o">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-5544" data-mp="23q1abl8788vs" />
              </button>}
          </div>
          <button type="button" onClick={onClose} className="h-11 px-1 text-sm font-bold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-5545" data-mp="28j44nvksvmd1">
            Cancel
          </button>
        </form>
      </header>

      <main className="absolute inset-x-0 bottom-0 top-[calc(env(safe-area-inset-top)+4.75rem)] overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] pt-5" data-id="element-5546" data-mp="2mhkcxqyb9kxa">
        {!searchQuery && recentSearches.length > 0 && <section aria-labelledby="recent-searches-heading" className="mb-6" data-id="element-5547" data-mp="23vih7n0aec5q">
            <div className="mb-3 flex items-center justify-between" data-id="element-5548" data-mp="2ilfxzhvvzzkw">
              <h2 id="recent-searches-heading" className="text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-5549" data-mp="2dm3cp1knz0kf">Recent searches</h2>
              <button type="button" onClick={clearRecentSearches} className="text-xs font-bold text-brand" data-id="element-5550" data-mp="1pvpltedtf7rf">Clear</button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-5551" data-mp="1kz8y2laj2vev">
              {recentSearches.map(search => <button key={search} type="button" onClick={() => selectSearch(search)} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-2 text-sm text-gray-700" data-id="element-5552" data-mp="1zslt5grz9nue">
                  <ClockIcon className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" data-id="element-5553" data-mp="3b80qwms0gjqk" />
                  {search}
                </button>)}
            </div>
          </section>}

        {!searchQuery && <section aria-labelledby="popular-searches-heading" className="mb-7" data-id="element-5554" data-mp="3itd0fsvdvry2">
            <h2 id="popular-searches-heading" className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-5555" data-mp="3ir9zidsp0eek">Popular searches</h2>
            <div className="flex flex-wrap gap-2" data-id="element-5556" data-mp="38unik03hm7ht">
              {POPULAR_SEARCHES.slice(0, 6).map(search => <button key={search} type="button" onClick={() => selectSearch(search)} className="rounded-full border border-sage-border bg-sage-soft px-3 py-2 text-xs font-bold text-sage-dark" data-id="element-5557" data-mp="gevf7jqi8x1">
                  {search}
                </button>)}
            </div>
          </section>}

        <section aria-labelledby="search-results-heading" data-id="element-5558" data-mp="3rsq8ch3v08go">
          <div className="mb-3 flex items-center justify-between gap-3" data-id="element-5559" data-mp="jehnjv7kwlz2">
            <h2 id="search-results-heading" className="text-base font-bold text-gray-900" data-id="element-5560" data-mp="jcepcnw9vmj9">
              {searchQuery ? `Results for “${searchQuery}”` : 'Suggested products'}
            </h2>
            <span className="shrink-0 text-xs text-gray-500" data-id="element-5561" data-mp="xyu53jw2oose">{matchingProducts.length} items</span>
          </div>
          {matchingProducts.length > 0 ? <div className="space-y-2.5" data-id="element-5562" data-mp="oz7592izrr1g">
              {matchingProducts.map(product => <button key={product.id} type="button" onClick={() => openProduct(product.id)} className="flex w-full items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 text-left shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-5563" data-mp="k03y9cjgbaqu">
                  <img src={product.image} alt="" className="h-14 w-14 shrink-0 rounded-lg border border-gray-100 object-cover" data-id="element-5564" data-mp="698w0j3gjrw8" />
                  <span className="min-w-0 flex-1" data-id="element-5565" data-mp="1byxmdy3c5w9">
                    <span className="block truncate text-sm font-bold text-gray-900" data-id="element-5566" data-mp="3sgyzknklon2t">{product.name}</span>
                    <span className="mt-0.5 block text-xs text-gray-500" data-id="element-5567" data-mp="3ni5ot2wp7a6c">{product.category}</span>
                    <span className="mt-1 block text-sm font-bold text-brand" data-id="element-5568" data-mp="3de7clr87n9kh">SAR {product.price.toFixed(2)}</span>
                  </span>
                  <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" data-id="element-5569" data-mp="3ibqpont69gau" />
                </button>)}
            </div> : <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center" data-id="element-5570" data-mp="3sbrbcgeik8i7">
              <SearchIcon className="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" data-id="element-5571" data-mp="2uoimqcj7ujbz" />
              <p className="mt-3 text-sm font-bold text-gray-900" data-id="element-5572" data-mp="2zlr6uinxcxfv">No matching products</p>
              <p className="mt-1 text-xs leading-5 text-gray-500" data-id="element-5573" data-mp="34l3sx16z20he">{noResultsMessage || 'Search by product name, category, or health need.'}</p>
            </div>}
        </section>
      </main>
    </div>;
}